"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import { XIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { Spinner } from "./spinner";
import type { Omittable } from "../../lib/props";

export const buttonVariants = cva(
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-(--button-gap) [--button-gap:--spacing(2)] whitespace-nowrap rounded-lg border font-medium text-base outline-none transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] pointer-coarse:after:absolute pointer-coarse:after:top-1/2 pointer-coarse:after:left-1/2 pointer-coarse:after:-translate-x-1/2 pointer-coarse:after:-translate-y-1/2 pointer-coarse:after:size-full pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-64 data-loading:select-none data-loading:text-transparent data-loading:[&_svg:not([data-slot=button-loading-indicator])]:invisible has-data-[slot=button-expansion]:transition-[color,background-color,border-color,box-shadow] has-data-[slot=button-expansion]:has-[[data-slot=avatar],[data-slot=entity-avatar]]:ps-px has-data-[slot=button-expansion]:has-[[data-slot=avatar],[data-slot=entity-avatar]]:not-data-expanded:pe-px has-data-[slot=button-expansion]:[&_:is([data-slot=avatar],[data-slot=entity-avatar])]:size-[calc(var(--button-h)-4px)] sm:text-sm [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:-mx-0.5 [&_svg]:shrink-0",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default:
          "h-9 px-[calc(--spacing(3)-1px)] sm:h-8 [--button-h:--spacing(9)] sm:[--button-h:--spacing(8)] [--button-pad:calc(--spacing(2)-1px)] has-data-[slot=button-expansion]:min-w-9 has-data-[slot=button-expansion]:px-(--button-pad) sm:has-data-[slot=button-expansion]:min-w-8",
        icon: "size-9 sm:size-8",
        "icon-lg": "size-10 sm:size-9",
        "icon-sm": "size-8 sm:size-7",
        "icon-xl":
          "size-11 sm:size-10 [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-4.5",
        "icon-xs":
          "size-7 rounded-md before:rounded-[calc(var(--radius-md)-1px)] sm:size-6 not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-4 sm:not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 px-[calc(--spacing(3.5)-1px)] sm:h-9 [--button-h:--spacing(10)] sm:[--button-h:--spacing(9)] [--button-pad:calc(--spacing(2.5)-1px)] has-data-[slot=button-expansion]:min-w-10 has-data-[slot=button-expansion]:px-(--button-pad) sm:has-data-[slot=button-expansion]:min-w-9",
        sm: "h-8 [--button-gap:--spacing(1.5)] px-[calc(--spacing(2.5)-1px)] sm:h-7 [--button-h:--spacing(8)] sm:[--button-h:--spacing(7)] [--button-pad:calc(--spacing(1.5)-1px)] has-data-[slot=button-expansion]:min-w-8 has-data-[slot=button-expansion]:px-(--button-pad) sm:has-data-[slot=button-expansion]:min-w-7",
        xl: "h-11 px-[calc(--spacing(4)-1px)] text-lg sm:h-10 sm:text-base [--button-h:--spacing(11)] sm:[--button-h:--spacing(10)] [--button-pad:calc(--spacing(3)-1px)] has-data-[slot=button-expansion]:min-w-11 has-data-[slot=button-expansion]:px-(--button-pad) sm:has-data-[slot=button-expansion]:min-w-10 [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-4.5",
        xs: "h-7 [--button-gap:--spacing(1)] rounded-md px-[calc(--spacing(2)-1px)] text-sm before:rounded-[calc(var(--radius-md)-1px)] sm:h-6 sm:text-xs [--button-h:--spacing(7)] sm:[--button-h:--spacing(6)] [--button-pad:calc(--spacing(2)-1px)] has-data-[slot=button-expansion]:min-w-7 sm:has-data-[slot=button-expansion]:min-w-6 [&_svg:not([class*='size-'])]:size-4 sm:[&_svg:not([class*='size-'])]:size-3.5",
      },
      variant: {
        default:
          "not-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] border-primary bg-primary text-primary-foreground shadow-primary/24 shadow-xs hover:bg-primary/90 data-pressed:bg-primary/90 *:data-[slot=button-loading-indicator]:text-primary-foreground [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none",
        destructive:
          "not-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] border-destructive bg-destructive text-white shadow-destructive/24 shadow-xs hover:bg-destructive/90 data-pressed:bg-destructive/90 *:data-[slot=button-loading-indicator]:text-white [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none",
        "destructive-outline":
          "border-input bg-popover not-dark:bg-clip-padding text-destructive-foreground shadow-xs/5 not-disabled:not-active:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/4%)] hover:border-destructive/32 hover:bg-destructive/4 data-pressed:border-destructive/32 data-pressed:bg-destructive/4 *:data-[slot=button-loading-indicator]:text-foreground dark:bg-input/32 dark:data-pressed:bg-input/64 dark:hover:bg-input/64 dark:not-disabled:before:shadow-[0_-1px_--theme(--color-white/2%)] dark:not-disabled:not-active:not-data-pressed:before:shadow-[0_-1px_--theme(--color-white/6%)] [:disabled,:active,[data-pressed]]:shadow-none",
        ghost:
          "border-transparent text-foreground hover:bg-wash-hover data-pressed:bg-wash-active *:data-[slot=button-loading-indicator]:text-foreground",
        link: "border-transparent text-foreground underline-offset-4 hover:underline data-pressed:underline *:data-[slot=button-loading-indicator]:text-foreground",
        outline:
          "border-input bg-popover not-dark:bg-clip-padding text-foreground shadow-xs/5 not-disabled:not-active:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/4%)] hover:bg-wash-hover data-pressed:bg-wash-active *:data-[slot=button-loading-indicator]:text-foreground dark:bg-input/32 dark:data-pressed:bg-input/64 dark:hover:bg-input/64 dark:not-disabled:before:shadow-[0_-1px_--theme(--color-white/2%)] dark:not-disabled:not-active:not-data-pressed:before:shadow-[0_-1px_--theme(--color-white/6%)] [:disabled,:active,[data-pressed]]:shadow-none",
        "primary-outline":
          "border-primary bg-primary/20 text-foreground shadow-xs/5 hover:bg-primary/15 data-pressed:bg-primary/15 *:data-[slot=button-loading-indicator]:text-foreground [:disabled,:active,[data-pressed]]:shadow-none",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/90 data-pressed:bg-secondary/90 *:data-[slot=button-loading-indicator]:text-secondary-foreground [:active,[data-pressed]]:bg-secondary/80",
      },
      shape: {
        pill: "rounded-full before:rounded-full",
      },
    },
  },
);

// ButtonExpansion needs the expanded state for the a11y tree (`aria-hidden`
// cannot be driven by CSS); everything visual keys off the root's
// `data-expanded` instead.
const ButtonExpandedContext = React.createContext(false);

export type ButtonProps = useRender.ComponentProps<"button"> &
  Omittable<{
  variant?: VariantProps<typeof buttonVariants>["variant"];
  size?: VariantProps<typeof buttonVariants>["size"];
  loading?: boolean;
  /** Reveals any `ButtonExpansion` child. Emitted as `data-expanded` on the
   *  root. */
  expanded?: boolean;
  /** Stadium/circle geometry for toolbar chips. Omit for the size's default
   *  rounding. */
  shape?: VariantProps<typeof buttonVariants>["shape"];
}>;

export function Button({
  className,
  variant,
  size,
  render,
  children,
  loading = false,
  expanded = false,
  shape,
  disabled: disabledProp,
  ...props
}: ButtonProps): React.ReactElement {
  const isDisabled: boolean = Boolean(loading || disabledProp);
  const typeValue: React.ButtonHTMLAttributes<HTMLButtonElement>["type"] =
    render ? undefined : "button";

  const defaultProps = {
    children: (
      <>
        {children}
        {/* Explicit md (16px) — the Spinner emits its own size-* class, so
            the button's descendant-svg pin no longer applies to it. */}
        <Spinner
          className="pointer-events-none absolute hidden in-data-loading:block"
          data-slot="button-loading-indicator"
          size="md"
        />
      </>
    ),
    className: cn(buttonVariants({ className, shape, size, variant })),
    "aria-disabled": loading || undefined,
    "data-expanded": expanded ? "" : undefined,
    "data-loading": loading ? "" : undefined,
    "data-slot": "button",
    disabled: isDisabled,
    type: typeValue,
  };

  return (
    <ButtonExpandedContext.Provider value={expanded}>
      {useRender({
        defaultTagName: "button",
        props: mergeProps<"button">(defaultProps, props),
        render,
      })}
    </ButtonExpandedContext.Provider>
  );
}

export type ButtonExpansionProps = useRender.ComponentProps<"span">;

/**
 * Content that slides open inside a Button when its `expanded` prop is set —
 * a revealed label, a count badge, a `ButtonClear`. Always in the DOM;
 * collapsed it is zero-width, faded, and `aria-hidden`, so expanding is a
 * pure CSS width interpolation (`grid-template-columns: 0fr → 1fr`) with no
 * measurement and no remount.
 */
export function ButtonExpansion({
  className,
  render,
  children,
  ...props
}: ButtonExpansionProps): React.ReactElement {
  const expanded = React.useContext(ButtonExpandedContext);

  // The collapse must be able to animate content the caller unmounts at the
  // same moment it stops being expanded (a grouping set back to none) — an
  // empty track has no width to interpolate from. Holding the last non-null
  // children keeps the outgoing content painted while the slot slides shut.
  const heldChildren = React.useRef(children);
  if (children != null) heldChildren.current = children;

  const defaultProps = {
    "aria-hidden": expanded ? undefined : true,
    children: (
      <span className="flex min-w-0 items-center gap-(--button-gap) overflow-hidden whitespace-nowrap">
        {children ?? heldChildren.current}
      </span>
    ),
    className: cn(
      "pointer-events-none grid [grid-template-columns:0fr] opacity-0 -ml-(--button-gap) transition-[grid-template-columns,opacity,margin] duration-100 ease-[cubic-bezier(0.215,0.61,0.355,1)] in-data-expanded:pointer-events-auto in-data-expanded:[grid-template-columns:1fr] in-data-expanded:opacity-100 in-data-expanded:ml-0",
      className,
    ),
    "data-slot": "button-expansion",
  };

  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(defaultProps, props),
    render,
  });
}

export type ButtonClearProps = useRender.ComponentProps<"span">;

/**
 * The clear (×) affordance for a toggle-style Button, composed as a direct
 * child (never inside `ButtonExpansion` — it must escape the slot's clip).
 * It overlays the trailing cap as the same nestled circle as a cap-filling
 * face — absolutely positioned, so it adds no width to the button — and
 * reveals only while the button is hovered and expanded. A real
 * button cannot nest inside a button, so it is a click target on a span
 * (not tab-focusable) that stops propagation — clearing never also presses
 * the button. Children default to the × glyph.
 */
export function ButtonClear({
  className,
  render,
  children,
  ...props
}: ButtonClearProps): React.ReactElement {
  const expanded = React.useContext(ButtonExpandedContext);

  const defaultProps = {
    "aria-hidden": expanded ? undefined : true,
    children: children ?? <XIcon />,
    className: cn(
      "pointer-events-none absolute end-px top-1/2 flex size-[calc(var(--button-h)-4px)] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground opacity-0 shadow-xs transition-opacity duration-100 hover:bg-primary/90 [[data-expanded]:hover_&]:pointer-events-auto [[data-expanded]:hover_&]:opacity-100",
      className,
    ),
    "data-slot": "button-clear",
    onClick: (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
    },
    role: "button",
    tabIndex: -1,
  };

  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(defaultProps, props),
    render,
  });
}
